/**
 * LTI v2 platform admin slice.
 *
 * Backs the LTI v2 admin REST surface at /api/lti2/admin/platforms.
 * Pagination uses continue-token semantics matching the Kubernetes API.
 *
 * Thunks intentionally surface backend errors via rejectWithValue so the
 * page can render the typed error_code from the RFC-7807-shaped body.
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axios";

const BASE = "/lti2/admin/platforms";

const initialState = {
  /** Current page of platforms. */
  items: [],
  /** Continue token for the next page; null/empty = end of list. */
  continueToken: "",
  /** History stack of continue tokens for "previous page" navigation. */
  pageHistory: [""],
  /** Filters applied client-side AND server-side (after pagination). */
  filters: { enabled: undefined, lmsKind: "" },
  /** Page size. */
  limit: 20,
  /** Loading + error flags by action. */
  loading: false,
  saving: false,
  error: null,
  /** The currently-selected platform, populated by getPlatform. */
  selected: null
};

const extractError = (err) => {
  const body = err?.response?.data;
  if (body?.code) return { code: body.code, detail: body.detail || body.code };
  return { code: "NETWORK_ERROR", detail: err?.message || "Request failed" };
};

export const listPlatforms = createAsyncThunk(
  "ltiV2Platforms/list",
  async ({ continueToken = "", limit = 20, enabled, lmsKind } = {}, thunkApi) => {
    try {
      const params = { limit };
      if (continueToken) params.continue = continueToken;
      if (typeof enabled === "boolean") params.enabled = enabled;
      if (lmsKind) params.lmsKind = lmsKind;
      const res = await axiosInstance.get(BASE, { params });
      return { ...res.data, requestedToken: continueToken };
    } catch (err) {
      return thunkApi.rejectWithValue(extractError(err));
    }
  }
);

export const getPlatform = createAsyncThunk(
  "ltiV2Platforms/get",
  async (name, thunkApi) => {
    try {
      const res = await axiosInstance.get(`${BASE}/${encodeURIComponent(name)}`);
      return res.data;
    } catch (err) {
      return thunkApi.rejectWithValue(extractError(err));
    }
  }
);

export const createPlatform = createAsyncThunk(
  "ltiV2Platforms/create",
  async (values, thunkApi) => {
    try {
      const res = await axiosInstance.post(BASE, values);
      thunkApi.dispatch(listPlatforms({}));
      return res.data;
    } catch (err) {
      return thunkApi.rejectWithValue(extractError(err));
    }
  }
);

export const updatePlatform = createAsyncThunk(
  "ltiV2Platforms/update",
  async ({ name, patch }, thunkApi) => {
    try {
      const res = await axiosInstance.patch(`${BASE}/${encodeURIComponent(name)}`, patch);
      thunkApi.dispatch(listPlatforms({}));
      return res.data;
    } catch (err) {
      return thunkApi.rejectWithValue(extractError(err));
    }
  }
);

export const deletePlatform = createAsyncThunk(
  "ltiV2Platforms/delete",
  async (name, thunkApi) => {
    try {
      await axiosInstance.delete(`${BASE}/${encodeURIComponent(name)}`);
      thunkApi.dispatch(listPlatforms({}));
      return name;
    } catch (err) {
      return thunkApi.rejectWithValue(extractError(err));
    }
  }
);

const slice = createSlice({
  name: "ltiV2Platforms",
  initialState,
  reducers: {
    setFilters(state, action) {
      state.filters = { ...state.filters, ...action.payload };
      state.pageHistory = [""];
      state.continueToken = "";
    },
    setLimit(state, action) {
      state.limit = action.payload || 20;
      state.pageHistory = [""];
      state.continueToken = "";
    },
    clearSelected(state) {
      state.selected = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(listPlatforms.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(listPlatforms.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items || [];
        state.continueToken = action.payload.continueToken || "";
        // Maintain page-history stack for back navigation.
        const req = action.payload.requestedToken || "";
        if (state.pageHistory[state.pageHistory.length - 1] !== req) {
          state.pageHistory.push(req);
        }
      })
      .addCase(listPlatforms.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || { code: "UNKNOWN", detail: action.error?.message };
      })
      .addCase(getPlatform.fulfilled, (state, action) => {
        state.selected = action.payload;
      })
      .addCase(getPlatform.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(createPlatform.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createPlatform.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(createPlatform.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(updatePlatform.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updatePlatform.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(updatePlatform.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      });
  }
});

export const { setFilters, setLimit, clearSelected } = slice.actions;
export default slice.reducer;
